import { Context } from '@nuxt/types'
import { computed, reactive, Ref, ref, unref, watch } from '@nuxtjs/composition-api'
import { MaybeRef } from '@vueuse/core'

import CarStore from '@/classes/CarStore'
import MapLocation from '@/classes/MapLocation'
import PricedCar from '@/classes/PricedCar'
import SearchFilter from '@/classes/SearchFilter'
import Store from '@/classes/Store'
import StoreCars from '@/classes/StoreCars'

import { parseActiveStore } from '@/modules/route-query'

import useI18n from '@/composables/use-i18n'
import DateHourRange from '@/classes/DateHourRange'
import { shuffle } from 'lodash'

// FIXME: 動態管理推薦店家列表
type AdSearchStore = {
  id: number,
  maxCarCount: number
}
const AD_SEARCH_STORE_IDX: AdSearchStore[] = [
  { id: 364, maxCarCount: 2 },
  { id: 505, maxCarCount: 2 },
  { id: 160, maxCarCount: 2 },
  { id: 284, maxCarCount: 2 },
  { id: 531, maxCarCount: 2 },
  { id: 310, maxCarCount: 2 },
  { id: 418, maxCarCount: 2 },
  { id: 70, maxCarCount: 2 },
  { id: 155, maxCarCount: 2 },
  { id: 269, maxCarCount: 2 },
  { id: 3, maxCarCount: 2 },
  { id: 220, maxCarCount: 2 },
  { id: 516, maxCarCount: 2 },
  { id: 485, maxCarCount: 2 },
  { id: 405, maxCarCount: 2 },
  { id: 487, maxCarCount: 2 },
  { id: 294, maxCarCount: 2 },
]

export default function (
  filter: MaybeRef<SearchFilter>,
  location: Ref<MapLocation | null> | MapLocation,
  dateHourRange: Ref<DateHourRange>
) {
  const origin = computed(() => unref(location)?.coordinate)

  // 推薦系統分辨每次查詢的 query id
  const qid = ref<string>()
  // 推薦系統使用的 ranker name
  const rankerName = ref<string>()

  /** Store 和 Car 列表的列表，是搜尋結果的唯一真相來源 */
  const storeCarsList = reactive({
    value: null as StoreCars[] | null,
    error: null as string | null,
    isFetching: false as boolean,
  })

  const isFuzzySearch = ref<boolean | null>(null)

  watch(() => storeCarsList.value, () => { storeCarsList.error = null })
  const setStoreCarsListFromApi = (data: any) => {
    if (!Array.isArray(data)) throw new TypeError('`data` must be an array')

    storeCarsList.value = data.map(({ store, cars }: {
      store: ConstructorParameters<typeof Store>[0],
      cars: ConstructorParameters<typeof PricedCar>[0][],
    }) => new StoreCars(
      cars.map(car => new PricedCar({
        ...car,
        dateHourRange: dateHourRange.value,
      })),
      store ? new Store(store, origin.value) : null
    ))
    isFuzzySearch.value = data.every(({ store }) => !store)
  }

  /**
   * 經篩選過的 storeCarsList
   * 適用於地圖以店家為單位之顯示
   */
  const filteredStoreCarsList = computed(() => {
    const { value: list } = storeCarsList
    if (!list) return null

    return list
      ?.map(({ store, cars }) => {
        return new StoreCars(
          cars.filter(car => unref(filter).test(car, store)),
          store
        )
      })
  })

  /**
   * CarStore（Car 和 Store 的組合）的列表
   * 適用於列表以車輛為單位顯示
   */
  const carStoreList = computed(() => {
    const { value: list } = storeCarsList
    if (!list) return null

    return list
      .map(({ cars, store }) => cars.map(car => new CarStore(car, store)))
      .flat()
  })

  /** 經「排序」和「篩選」過的列表 */
  const filteredCarStoreList = computed(() => {
    const { value: list } = carStoreList
    if (!list) return null

    const { sortMethod: method } = unref(filter)
    if (!method) return [...list]

    return list
      .sort((a, b) => {
        if (!(a && b && a?.store && b?.store)) return 1
        switch (method) {
          case 'distanceAsc':
            return a?.store?.distance - b?.store?.distance
          case 'distanceDesc':
            return b?.store?.distance - a?.store?.distance
          case 'priceAsc':
            return Number(a?.car?.price) - Number(b?.car?.price)
          case 'priceDesc':
            return Number(b?.car?.price) - Number(a?.car?.price)
          case 'ratingDesc':
            return (b?.store?.rating?.score ?? 0) - (a?.store?.rating?.score ?? 0)
          case 'rankDesc':
            return Number(b?.car?.rankScore) - Number(a?.car?.rankScore)
          default:
            return 1
        }
      })
      .filter(carStore => unref(filter).test(carStore))
  })

  /** 經店家廣告排序後的列表 */
  const adSortedCarStoreList = computed(() => {
    const filteredList = filteredCarStoreList.value
    if (!filteredList) return null

    /**
     * 廣告車輛的店家列表
     */
    const filteredStoreMap = new Map<number, Store>(filteredList.filter(({ store }) => !!store).map(({ store }) => [store!.id, store!]))
    const adStores = AD_SEARCH_STORE_IDX.map(adSearchStore => filteredStoreMap.get(adSearchStore.id)).filter(Boolean) as Store[]
    /**
     * 篩選出距離搜尋地點最近的前三家店
     */
    const distanceSortedAdStores = adStores.sort((a, b) => a.distance - b.distance).slice(0, 3)
    /**
     * 重新建立 『商家』 與 『最大呈現車輛數』 的映射
     */
    const adSearchStores: AdSearchStore[] = distanceSortedAdStores.map(store => ({
      id: store.id,
      maxCarCount: AD_SEARCH_STORE_IDX.find(adSearchStore => adSearchStore.id === store.id)?.maxCarCount ?? 0,
    }))

    /**
     * 迭代 filteredList，並根據商家 id 和最大車輛數限制，將車輛分配到廣告列表或剩餘列表中
     */
    const { adList, remainingList } = filteredList.reduce((acc, carStore) => {
      const storeId = carStore.store?.id
      if (!storeId) {
        acc.remainingList.push(carStore)
        return acc
      }
      const maxCount = adSearchStores.find(ad => ad.id === storeId)?.maxCarCount
      if (!maxCount) {
        acc.remainingList.push(carStore)
        return acc
      }
      const currentCount = acc.storeCount.get(storeId) || 0

      // 如果當前店家車輛數小於最大車輛數，則將車輛加入推薦列表
      if (currentCount < maxCount) {
        acc.storeCount.set(storeId, currentCount + 1)
        acc.adList.push(new CarStore(carStore.car, carStore.store, true))
      } else {
        acc.remainingList.push(carStore)
      }

      return acc
    }, {
      adList: [] as CarStore[],
      remainingList: [] as CarStore[],
      storeCount: new Map<number, number>(),
    })

    const shuffledAdList = shuffle(adList)

    return [...shuffledAdList, ...remainingList]
  })

  /** 實際顯示的車輛列表 */
  const displayedCarStoreList = computed(() =>
    !isFuzzySearch.value
      ? adSortedCarStoreList.value
      : carStoreList.value
  )

  /** 「篩選後」的列表長度 */
  const count = computed(() =>
    unref(adSortedCarStoreList)?.length ?? null
  )

  /** 從車輛列表算出可供選擇的價格區間（供篩選條件總價格之拉桿使用） */
  const priceRangeMinMax = computed(() => {
    const { value: list } = carStoreList
    if (!list) return null

    const prices = list.map(({ car }) => Number(car.price))
    if (!prices.length) return null
    const min = Math.min(...prices)
    const max = Math.max(...prices)

    return [min, max] as const
  })

  /** 選中的 StoreId */
  const activeStoreId = ref<number | string | null>(null)
  const setActiveStoreIdFromQuery = (
    query: MaybeRef<Context['query']>
  ) => {
    activeStoreId.value = parseActiveStore(unref(query)) ?? null
  }

  /** 根據 carStoreList 取得不重複的廠牌 id 列表 */
  const brandIds = computed(() =>
    [...new Set(
      (carStoreList.value ?? [])
        .map(({ car }) => car.brand?.id)
        .filter(Boolean)
    )] as number[]
  )

  /** 根據 carStoreList 取得不重複的車型 id 列表 */
  const carTypeIds = computed(() =>
    [...new Set(
      (carStoreList.value ?? [])
        .map(({ car }) => car.type?.id)
        .filter(Boolean)
    )] as number[]
  )

  const stateMessage = ref<{ icon: string, message: string, showClearFilterButton?: boolean } | null>(null)

  // 開始找車時清空 `stateMessage`
  watch(
    () => storeCarsList.isFetching,
    () => { stateMessage.value = null }
  )

  const i18n = useI18n()

  watch(count, (count) => {
    // 因為套用了篩選條件而變得沒結果的情況下
    if (carStoreList.value?.length && !count) {
      stateMessage.value = {
        icon: 'sad-magnifier',
        message: String(i18n.t('message.noResultDueToFilter')),
        showClearFilterButton: true,
      }
    } else if (!isFuzzySearch.value) {
      stateMessage.value = null
    }
  })

  return {
    qid,
    rankerName,
    storeCarsList,
    isFuzzySearch,
    setStoreCarsListFromApi,

    filteredStoreCarsList,
    activeStoreId,
    setActiveStoreIdFromQuery,

    carStoreList,
    filteredCarStoreList,
    displayedCarStoreList,

    count,
    priceRangeMinMax,

    brandIds,
    carTypeIds,

    stateMessage,
  }
}
